// pages/inventory/StoreReceiptNote.jsx
// ✅ Converted from: StoreReceiptNote.aspx + StoreReceiptNote.aspx.cs + Inv_StoreReceipt.cs
// ✅ ALL logic preserved: Direct Purchase / Own Workshop / Returned Items
// ✅ FIFO future date check, Serial No popup (new tab), Qty/Price validation
// ✅ Edit mode (receiptId from query param), Autocomplete for item code/name
// ✅ Total Value auto-calculation on checkbox/qty/price change
// ✅ Pagination added on gv_branchitemmaster (detail grid)
// ✅ NO rights logic (excluded as instructed)
// ✅ Follows exact same structure/style as StoreReceiptListing.jsx template

import { useEffect, useContext, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft, Plus, Send, RefreshCcw, Search,
  Hash, Calendar, Store, FolderKanban,
  CheckSquare, Tag, Code2, AlignLeft, ExternalLink
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";

// ─────────────── helpers ───────────────
const s = (v) => (v ?? "").toString().trim();
const toInt = (v, def = 0) => { const n = Number(v); return Number.isFinite(n) ? n : def; };
const toDecimal = (v, def = 0) => { const n = parseFloat(v); return Number.isFinite(n) ? n : def; };

// dd/MM/yyyy ↔ YYYY-MM-DD helpers
const isoToDate = (iso) => {
  const x = s(iso); if (!x) return null;
  const m = x.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};
const dmyToIso = (dmy) => {
  const x = s(dmy); if (!x) return "";
  const m = x.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};
const isoToDmy = (iso) => {
  const d = isoToDate(iso); if (!d) return "";
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const isoAddDays = (iso, days) => {
  const d = isoToDate(iso) || new Date(); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const roundTwo = (num) => Math.round(num * 100) / 100;

// ─────────────── Receipt Type constants ───────────────
const RT = { DIRECT: "P", RETURNED: "R", WORKSHOP: "W" };

// ─────────────── Page sizes ───────────────
const DETAIL_PAGE_SIZE = 10; // pagination on detail grid

export default function StoreReceiptNote() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // Session-like values from localStorage (same pattern as template)
  const branchId      = localStorage.getItem("branchId")            || "";
  const branchType    = localStorage.getItem("branchType")          || "";
  const loginUserId   = localStorage.getItem("userId")              || "";
  const finYearId     = localStorage.getItem("finYearId")           || "";
  const finYearStart  = localStorage.getItem("financeYearStartDate")|| ""; // dd/MM/yyyy
  const finYearEnd    = localStorage.getItem("financeYearEndDate")  || ""; // dd/MM/yyyy
const [serialNosMap, setSerialNosMap] = useState({});
  // Read receiptId from query string → edit mode (ASP: Session["ReceiptID"])
  const qp = new URLSearchParams(window.location.search);
  const editReceiptId = toInt(qp.get("receiptId"), 0);
  const isEditMode = editReceiptId > 0;

  // ─── form state ───
  const [receiptType, setReceiptType]       = useState(RT.RETURNED); // default Returned
  const [receiptNo, setReceiptNo]           = useState("");           // lbl_storereceiptno
  const [entryDate, setEntryDate]           = useState(todayIso());   // txt_date
  const [storeId, setStoreId]               = useState("Select");     // ddl_store
  const [projectId, setProjectId]           = useState("Select");     // ddl_projectcode
  const [isApproved, setIsApproved]         = useState(false);        // fld_IsApproved

  // ─── dropdowns ───
  const [storeList, setStoreList]           = useState([]);
  const [projectList, setProjectList]       = useState([]);
  const [categoryList, setCategoryList]     = useState([]);

  // ─── filtration area ───
  const [categoryId, setCategoryId]         = useState("Select");
  const [itemCode, setItemCode]             = useState("");
  const [itemName, setItemName]             = useState("");
  const [fromDate, setFromDate]             = useState(isoAddDays(todayIso(), -7));
  const [toDate, setToDate]                 = useState(todayIso());

  // ─── autocomplete ───
  const [acCodeItems, setAcCodeItems]       = useState([]);
  const [acNameItems, setAcNameItems]       = useState([]);
  const [acCodeOpen, setAcCodeOpen]         = useState(false);
  const [acNameOpen, setAcNameOpen]         = useState(false);

  // ─── gv_items (workshop / returned issue list) ───
  const [issueRows, setIssueRows]           = useState([]);

  // ─── lst_items (direct purchase listbox) ───
  const [listBoxItems, setListBoxItems]     = useState([]);
  const [selectedListBox, setSelectedListBox] = useState(new Set());

  // ─── gv_branchitemmaster (detail grid) ───
  const [detailRows, setDetailRows]         = useState([]);           // full dataset
  const [detailPage, setDetailPage]         = useState(1);
  const detailTotalPages = Math.max(1, Math.ceil(detailRows.length / DETAIL_PAGE_SIZE));
  const pagedDetailRows = useMemo(() => {
    const start = (detailPage - 1) * DETAIL_PAGE_SIZE;
    return detailRows.slice(start, start + DETAIL_PAGE_SIZE);
  }, [detailRows, detailPage]);

  // ─── info label (FIFO message) ───
  const [infoMessage, setInfoMessage]       = useState("");

  // ─── total value ───
  const [totalValue, setTotalValue]         = useState("0.00");

  // ─── loading ───
  const [loading, setLoading]               = useState(false);
  const [submitting, setSubmitting]         = useState(false);

  // Visibility flags (match ASP panel visibility logic exactly)
  const showFiltration   = !isEditMode && (receiptType === RT.DIRECT || receiptType === RT.RETURNED);
  const showFromToDate   = !isEditMode && receiptType === RT.RETURNED;
  const showCategory     = !isEditMode && receiptType === RT.DIRECT;
    const showIssueGrid = !isEditMode && (
    receiptType === RT.WORKSHOP || receiptType === RT.RETURNED
  );
  const showListBox      = !isEditMode && receiptType === RT.DIRECT;
  const showDetailGrid   = detailRows.length > 0 || isEditMode;
  const showSubmitBtn    = detailRows.length > 0 || isEditMode;

  // ─── Styling helpers (exact same pattern as template) ───
  const primaryBtnClass  = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass= `hover:bg-${baseColor}-700`;
  const inputBase =
    `w-full px-4 py-2.5 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");
  const selectBase =
    `w-full px-4 py-2.5 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500");
  const labelBase    = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;
  const cardBase     =
    `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");
  const primaryBtn   =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-md ` +
    `hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 text-sm ${primaryBtnClass} ${primaryHoverClass}`;
  const ghostBtn     =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold border transition-all duration-200 text-sm ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");
  const thClass      = `px-3 py-3 text-xs font-bold uppercase tracking-wide text-white text-left whitespace-nowrap`;
  const tdClass      = `px-3 py-2.5 text-sm whitespace-nowrap ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ═══════════════════════════════════════════════════════
  //  CALCULATIONS — mirrors JS Calculation() in aspx
  // ═══════════════════════════════════════════════════════
  const recalcTotal = useCallback((rows) => {
    let sub = 0;
    rows.forEach(r => {
      if (r.IsSelected) {
        const qty   = toDecimal(r.Qty,   0);
        const price = toDecimal(r.Price, 0);
        sub += qty * price;
      }
    });
    setTotalValue(roundTwo(sub).toFixed(2));
  }, []);

  // update a detail row field and recalc
  const updateDetailRow = (globalIndex, field, value) => {
    setDetailRows(prev => {
      const next = [...prev];
      next[globalIndex] = { ...next[globalIndex], [field]: value };
      recalcTotal(next);
      return next;
    });
  };

  // toggle checkbox on a detail row
  const toggleDetailSelect = (globalIndex, checked) => {
    updateDetailRow(globalIndex, "IsSelected", checked);
  };

  // select-all (only for non-disabled rows)
  const handleSelectAll = (checked) => {
    setDetailRows(prev => {
      const next = prev.map(r => r._chkDisabled ? r : { ...r, IsSelected: checked });
      recalcTotal(next);
      return next;
    });
  };

  // ═══════════════════════════════════════════════════════
  //  DATE VALIDATION — mirrors checkDate + Check_from_to
  // ═══════════════════════════════════════════════════════
  const validateEntryDate = (iso) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const d = isoToDate(iso);
    if (!d) return false;
    if (d > today) { alert("You can select only Previous days"); return false; }
    if (finYearStart) {
      const fyStart = isoToDate(dmyToIso(finYearStart));
      if (fyStart && d < fyStart) {
        alert(`You can select only Greater than or Equal to Financial Year Date  ${finYearStart}`);
        return false;
      }
    }
    if (finYearEnd) {
      const fyEnd = isoToDate(dmyToIso(finYearEnd));
      if (fyEnd && d > fyEnd) {
        alert(`You can select only Less than or Equal to Financial Year End Date  ${finYearEnd}`);
        return false;
      }
    }
    return true;
  };

  const validateFromTo = () => {
    const today = new Date(); today.setHours(0,0,0,0);
    const fd = isoToDate(fromDate);
    const td = isoToDate(toDate);
    if (fd && fd > today) { alert("Select from date lesser than current date"); return false; }
    if (td && td > today) { alert("Select to date lesser than or Equal to Current date"); return false; }
    if (fd && td && td < fd) { alert("Please select to date greater than from date"); return false; }
    return true;
  };

  // ═══════════════════════════════════════════════════════
  //  INIT — Page_Load (!IsPostBack) and Edit mode
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    loadDropdowns();
    if (isEditMode) {
      loadEditDetails(editReceiptId);
    } else {
      loadProjects();
      // Workshop: auto-load workshop items on mount
      if (receiptType === RT.WORKSHOP) loadWorkshopItems();
    }
    // eslint-disable-next-line
  }, []);

  // When receipt type changes (radio), run SetVisibility logic
  useEffect(() => {
    if (isEditMode) return;

    // Reset all dependent state when type changes
    setDetailRows([]);
    setListBoxItems([]);
    setSelectedListBox(new Set());
    setIssueRows([]);
    setInfoMessage("");
    setTotalValue("0.00");
    setSelectedIssueId(null);

    if (receiptType === RT.RETURNED) {
      setFromDate(isoAddDays(todayIso(), -7));
      setToDate(todayIso());
      // Returned: Don't auto-search — user must click Search or enter filters
      // The filtration area (From/To + Search button) shows via showFiltration flag ✅
    }

    if (receiptType === RT.WORKSHOP) {
      // Small timeout ensures state resets have applied before API call
      setTimeout(() => loadWorkshopItems(), 50);
    }

    // eslint-disable-next-line
  }, [receiptType]);


  // When category changes → search (ddl_itemcategory_SelectedIndexChanged)
  useEffect(() => {
    if (isEditMode) return;
    if (categoryId !== "Select" || itemCode || itemName) doSearch();
    // eslint-disable-next-line
  }, [categoryId]);

  // ═══════════════════════════════════════════════════════
  //  API CALLS
  // ═══════════════════════════════════════════════════════

  // FillDDL()
  const loadDropdowns = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/dropdowns`, {
        params: { branchId, finYearId }
      });
      if (res.data?.success) {
        setStoreList(res.data.stores || []);
        setCategoryList(res.data.categories || []);
        if (res.data.stores?.length > 0 && storeId === "Select") {
          // don't auto-select; user must choose
        }
      }
    } catch (e) { toast.error("Failed to load dropdowns"); }
  };

  // fillproject()
  const loadProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/projects`, {
        params: { branchId, branchType, finYearId }
      });
      if (res.data?.success) setProjectList(res.data.projects || []);
    } catch { /* silent */ }
  };

  // GetWorkShopItems() — usp_inv_SR_GetWorkshopMasDet
  const loadWorkshopItems = async () => {
    try {
      setLoading(true);
      setIssueRows([]); // clear first

      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/workshop-items`, {
        params: {
          branchId,
          finYearId,
          // pass storeId only if selected — SP handles filtering
          storeId: storeId !== "Select" ? storeId : undefined,
        }
      });

      if (res.data?.success) {
        const rows = res.data.rows || [];
        setIssueRows(rows);
        if (rows.length === 0) {
          // Don't leave user with blank — show info
          toast.info("No workshop entries found for this branch.", { autoClose: 3000 });
        }
      } else {
        setIssueRows([]);
        toast.warn(res.data?.message || "No workshop items found.");
      }
    } catch (e) {
      setIssueRows([]);
      toast.error("Failed to load workshop items: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  // EditDetails(ReceiptID) — usp_inv_SR_GetEditDetailsinSRN
  const loadEditDetails = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/edit-details`, {
        params: { receiptId: id, finYearId }
      });
      if (res.data?.success && res.data.data?.length > 0) {
        const d = res.data.data[0];
        const rtype = s(d.fld_StoreReceiptType);
        setReceiptType(rtype);
        setStoreId(s(d.fld_storeid));
        setTotalValue(s(d.fld_TotalValue));
        setEntryDate(dmyToIso(s(d.fld_Date)) || todayIso());
        setReceiptNo(s(d.fld_branchwiseno));
        setIsApproved(d.fld_IsApproved === true || d.fld_IsApproved === "true" || d.fld_IsApproved === 1);

        // Build detail rows
        console.log("test", res.data.data);
        const rows = res.data.data.map(row => ({
          
          
          DetID:           toInt(row.DetID),
          ID:              toInt(row.ID),
          fld_itemid:      toInt(row.fld_itemid),
          ItemCode:        s(row.ItemCode),
          ItemName:        s(row.ItemName),
          PartNo:          s(row.PartNo),
          fld_uomname:     s(row.fld_Uomname),
          IssuedQty:       s(row.IssuedQty),
          Qty:             s(row.Qty),
          Price:           s(row.Price),
          Department:      s(row.Department),
          Vehicle:         s(row.Vehicle),
          fld_IsSerialNo:  toInt(row.fld_IsSerialNo),
          serialno:        s(row.serialno),
          fld_ReceiveFrom: s(row.fld_receivefrom),
          IsSelected:      true,
          _chkDisabled:    false,
        }));
        setDetailRows(rows);
        recalcTotal(rows);

        await loadProjects();
        if (d.fld_projectid) setProjectId(s(d.fld_projectid));
      }
    } catch (e) { toast.error("Failed to load edit details"); }
    finally { setLoading(false); }
  };

  // Autocomplete — GetCompletionList (item code)
  const fetchAcCode = async (prefix) => {
    if (!prefix) { setAcCodeItems([]); setAcCodeOpen(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/autocomplete`, {
        params: { type: "Code", branchId, categoryId, itemCode, itemName, prefix, receiptType, finYearId }
      });
      if (res.data?.success) { setAcCodeItems(res.data.items || []); setAcCodeOpen(true); }
    } catch { setAcCodeItems([]); }
  };

  // Autocomplete — GetCompletionList (item name)
  const fetchAcName = async (prefix) => {
    if (!prefix) { setAcNameItems([]); setAcNameOpen(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/autocomplete`, {
        params: { type: "Name", branchId, categoryId, itemCode, itemName, prefix, receiptType, finYearId }
      });
      if (res.data?.success) { setAcNameItems(res.data.items || []); setAcNameOpen(true); }
    } catch { setAcNameItems([]); }
  };

  // search() — usp_inv_SR_GetItemsforListBox / usp_inv_SR_GetIssueNoforGrid
const doSearch = async () => {
    if (receiptType === RT.RETURNED && !validateFromTo()) return;

    // For Direct: need at least one filter
    if (receiptType === RT.DIRECT &&
        !itemCode && !itemName && categoryId === "Select") {
      toast.info("Please enter Item Code, Item Name or select a Category to search.");
      return;
    }

    try {
      setLoading(true);
      setIssueRows([]);
      setListBoxItems([]);
 
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/search`, {
        params: {
          branchId,
          finYearId,
          receiptType,
          categoryId: categoryId !== "Select" ? categoryId : "%",
          itemCode:   itemCode  || "%",
          itemName:   itemName  || "%",
          storeId:    storeId !== "Select" ? storeId : "0",
          fromDate:   isoToDmy(fromDate),
          toDate:     isoToDmy(toDate),
        }
      });

      if (res.data?.success) {
        if (receiptType === RT.DIRECT) {
          const items = res.data.rows || [];
          setListBoxItems(items);
          if (items.length === 0) toast.info("No items found for the given filters.");
        } else {
          // Returned Items → show in issue grid
          const rows = res.data.rows || [];
          setIssueRows(rows);
          if (rows.length === 0) toast.info("No returned issue entries found for the given filters.");
        }
      } else {
        toast.warn(res.data?.message || "No results found.");
      }
    } catch (e) {
      toast.error("Search failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  // on_txtChanged — fires when item code/name textbox changes
  const handleItemFilterChange = () => {
    if (itemCode || itemName || categoryId !== "Select") doSearch();
  };

  // gv_items row click — WorkShopNo / returned issue
  // usp_inv_SR_GetWorkShopItemDetails / usp_inv_SR_BindIssueItemsDetailsinGrid
  const handleIssueRowClick = async (row) => {
    const id = toInt(row.ID);
    try {
      setLoading(true);
      const endpoint = receiptType === RT.WORKSHOP
        ? "workshop-item-details"
        : "returned-item-details";
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/${endpoint}`, {
        params: { branchId, id, finYearId }
      });
      if (res.data?.success) {
        const rows = (res.data.rows || []).map(r => ({
          DetID:           toInt(r.DetID),
          ID:              toInt(r.ID),
          fld_itemid:      toInt(r.fld_itemid),
          ItemCode:        s(r.ItemCode),
          ItemName:        s(r.ItemName),
          PartNo:          s(r.PartNo),
          fld_uomname:     s(r.fld_uomname),
          IssuedQty:       s(r.IssuedQty),
          Qty:             s(r.Qty),
          Price:           s(r.Price),
          Department:      s(r.Department),
          Vehicle:         s(r.Vehicle),
          fld_IsSerialNo:  toInt(r.fld_IsSerialNo),
          serialno:        s(r.serialno),
          fld_ReceiveFrom: s(r.fld_ReceiveFrom),
          IsSelected:      true,
          _chkDisabled:    false,
          _workshopQtyDisabled: receiptType === RT.WORKSHOP,
        }));
        setDetailRows(rows);
        recalcTotal(rows);
        await getFifoFutureDateItems(rows);
      }
    } catch { toast.error("Failed to load items"); }
    finally { setLoading(false); }
  };

  // img_add_Click — add selected listbox items
  // usp_inv_SR_AddItemsForGrid
  const handleAddItems = async () => {
    if (listBoxItems.length === 0) { alert("Please Search Items To Add"); return; }
    if (selectedListBox.size === 0) { alert("Please Select Any Items"); return; }

    const selected = [...selectedListBox];
    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/inventory/store-receipt-note/add-items`, {
        branchId, storeId, finYearId,
        itemCodes: selected,
        existingItemIds: detailRows.map(r => r.fld_itemid),
      });
      if (res.data?.success) {
        const incoming = (res.data.rows || []).map(r => ({
          DetID:           0,
          ID:              0,
          fld_itemid:      toInt(r.fld_itemid),
          ItemCode:        s(r.ItemCode),
          ItemName:        s(r.ItemName),
          PartNo:          s(r.PartNo),
          fld_uomname:     s(r.fld_uomname),
          IssuedQty:       "0",
          Qty:             s(r.Qty) || "0",
          Price:           s(r.Price) || "0",
          Department:      "",
          Vehicle:         "",
          fld_IsSerialNo:  toInt(r.fld_IsSerialNo),
          serialno:        s(r.serialno),
          fld_ReceiveFrom: "",
          IsSelected:      false,
          _chkDisabled:    false,
        }));
        // merge — don't duplicate (ASP logic: ImportRow if itemid not already present)
        const merged = [...detailRows];
        incoming.forEach(inc => {
          if (!merged.find(x => x.fld_itemid === inc.fld_itemid)) merged.push(inc);
        });
        setDetailRows(merged);
        await getFifoFutureDateItems(merged);
      }
    } catch { toast.error("Failed to add items"); }
    finally { setLoading(false); }
  };

  // GetFifoFutureDateItems() — usp_inv_GetFutureDateFifoItems
  const getFifoFutureDateItems = async (rows) => {
    if (rows.length === 0) return;
    const itemids = rows.map(r => r.fld_itemid).join(",");
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-note/fifo-future-check`, {
        params: { branchId, storeId, itemids, entryDate: isoToDmy(entryDate), finYearId }
      });
      if (res.data?.success) {
        const futureIds = new Set((res.data.futureItemIds || []).map(String));
        if (futureIds.size > 0) {
          setInfoMessage("UnChecked Items Already Issued on FutureDate. So you cant purchase items before the Issue Date");
          setDetailRows(prev => {
            const next = prev.map(r => ({
              ...r,
              IsSelected:   !futureIds.has(String(r.fld_itemid)),
              _chkDisabled: futureIds.has(String(r.fld_itemid)),
            }));
            recalcTotal(next);
            return next;
          });
        } else {
          setInfoMessage("");
          setDetailRows(prev => {
            const next = prev.map(r => ({ ...r, IsSelected: true, _chkDisabled: false }));
            recalcTotal(next);
            return next;
          });
        }
      }
    } catch { /* silent */ }
  };

  // txt_date changed → getFifoFutureDateItems
  const handleEntryDateChange = async (iso) => {
    if (!validateEntryDate(iso)) return;
    setEntryDate(iso);
    if (detailRows.length > 0) await getFifoFutureDateItems(detailRows);
  };

  // Serial No popup — gv_branchitemmaster_RowCommand "serialentry"
  // mirrors ASP window.open(SerialNoEntry.aspx / SerialNo_StoreReceiptIssueandWorkshop.aspx)
  const handleSerialNoClick = (row, rowIndex) => {
    const qty = s(row.Qty);
    if (!qty || qty === "0") { alert("Please enter accepted qty and try again"); return; }
    if (storeId === "Select") { alert("Please enter store"); return; }
    if (qty.includes(".")) { alert("Cant generate serial no for decimal values"); return; }

    let url = "";
    if (receiptType === RT.DIRECT) {
      if (!isEditMode) {
         url = `/inventory/SerialNoEntry?item=${itemCode}-${storeId}-${branchId}-${editReceiptId}-${rowIndex}&stockQty=${qty}&type=GRN&flag=2&finYearId=${finYearId}`;
  // Better: pass receiptCode directly so Node doesn't need to look it up
      } else {
        url = `/inventory/SerialNoEntry?item=${row.ItemCode}-${storeId}-${branchId}-${editReceiptId}-${rowIndex}&stockQty=${qty}&type=GRN&flag=2`;
      }
    } else {
      const flag = isEditMode ? 2 : 1;
      url = `/inventory/SerialNo_StoreReceiptIssueandWorkshop?item=${row.fld_itemid}-${storeId}-${branchId}-${row.ID}-${rowIndex}-${row.Department}-${row.Vehicle}&stockQty=${qty}&type=SI&flag=${flag}`;
    }
    window.open(url, "_blank", "width=800,height=800,scrollbars=yes,menubar=no,resizable=no");
  };

  // ═══════════════════════════════════════════════════════
  //  VALIDATION — IsValidData()
  // ═══════════════════════════════════════════════════════
  const isValidData = () => {
    if (!entryDate) { alert("Please Enter Date"); return false; }

    const checkedRows = detailRows.filter(r => r.IsSelected);
    if (checkedRows.length === 0) { alert("Please Select Any One"); return false; }

    for (const r of checkedRows) {
      const qty   = s(r.Qty);
      const price = s(r.Price);

      if (!qty || qty === "") { alert("Please Enter Qty"); return false; }
      if (qty === "." || toDecimal(qty) === 0) { alert("Please Enter Valid Qty"); return false; }
      if (!price || price === "") { alert("Please Enter Price"); return false; }
      if (price === "." || toDecimal(price) === 0) { alert("Please Enter Valid Price"); return false; }

      // Returned: qty must be ≤ issued qty
      if (receiptType === RT.RETURNED) {
        const issQty = toDecimal(r.IssuedQty, 0);
        const entQty = toDecimal(qty, 0);
        if (entQty > issQty) {
          alert("Qty should be less than Or Equal Issued Qty");
          return false;
        }
      }
    }
    return true;
  };

  // ═══════════════════════════════════════════════════════
  //  SUBMIT — ImgBtn_StatutoryRemove_Click
  //  usp_inv_SR_InsertStoreReceiptMas + all detail inserts
  // ═══════════════════════════════════════════════════════
  const handleSubmit = async () => {
    if (!isValidData()) return;

    const ok = window.confirm("Do you want to save this Store Receipt Note?");
    if (!ok) return;

    try {
      setSubmitting(true);

      let receiptTypeCode = receiptType;
      let storeIssueId = null;
      let workshopId   = null;

      // ASP: storesissueid = HdnSelectedRowIndex (the clicked issue/workshop row ID)
      // We store the selected issue grid row ID when user clicks it
      if (receiptType === RT.RETURNED && selectedIssueId) storeIssueId = selectedIssueId;
      if (receiptType === RT.WORKSHOP  && selectedIssueId) workshopId   = selectedIssueId;

      const payload = {
        finYearId,
        branchId,
        loginUserId,
        receiptType:    receiptTypeCode,
        entryDate:      isoToDmy(entryDate),
        storeId:        storeId !== "Select" ? storeId : null,
        projectId:      projectId !== "Select" ? projectId : null,
        totalValue,
        storeIssueId,
        workshopId,
        isUpdate:       isEditMode,
        receiptId:      isEditMode ? editReceiptId : 0,
        serialItems: Object.entries(serialNosMap).flatMap(([, snos]) => snos),

        // detail rows (only checked)
        items: detailRows
          .filter(r => r.IsSelected)
          .map(r => ({
            DetID:        r.DetID,
            ID:           r.ID,
            fld_itemid:   r.fld_itemid,
            IssuedQty:    r.IssuedQty || "0",
            Qty:          r.Qty,
            Price:        r.Price,
            Department:   r.Department,
            Vehicle:      r.Vehicle,
            fld_ReceiveFrom: r.fld_ReceiveFrom,
            serialno:     r.serialno,
          }))
      };

      const res = await axios.post(`${BASE_URL}/api/inventory/store-receipt-note/submit`, payload);
      if (res.data?.success) {
        alert(res.data.message || "Store Receipt Inserted Successfully");
        if ((res.data.message || "").includes("Successfully")) {
          window.location.href = "/inventory/StoreReceipt_Listing";
        }
      } else {
        alert(res.data?.message || "Please Try Again");
      }
    } catch (e) {
      alert(e?.response?.data?.message || "Please Try Again");
    } finally {
      setSubmitting(false);
    }
  };

  // img_Back_Click
  const handleBack = () => {
    window.location.href = "/inventory/StoreReceipt_Listing";
  };

  // Track which issue row was clicked (for storeIssueId / workshopId)
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const handleIssueRowClickFull = async (row) => {
    setSelectedIssueId(toInt(row.ID));
    await handleIssueRowClick(row);
  };

  // Listbox multi-select toggle
  const toggleListBox = (item) => {
    setSelectedListBox(prev => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
  };

  // Page buttons for detail grid pagination
  const detailPageButtons = useMemo(() => {
    const tp = detailTotalPages; const win = 5;
    let start = Math.max(1, detailPage - Math.floor(win/2));
    let end = Math.min(tp, start + win - 1);
    if (end - start < win - 1) start = Math.max(1, end - win + 1);
    const arr = []; for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [detailPage, detailTotalPages]);

  // ═══════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <LayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} hideProgressBar={false}
        closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-4 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">STORE RECEIPT NOTE</span>}
          setSkin={setSkin} currentSkin={skin}
        />

        <div className={`mx-auto max-w-7xl p-5 mt-5 rounded-3xl shadow-2xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `${currentSkin.glass} bg-white text-black border-gray-200`
        }`}>

          {/* ── Header row ── */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Store className={isDark ? "text-white" : "text-gray-700"} size={20} />
              </div>
              <div>
                <div className={`text-xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Store Receipt Note
                </div>
                <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                  {isEditMode ? `Editing Receipt: ${receiptNo}` : "New Entry"}
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                INFRA SCREEN : A-32/2
              </div>
            </div>
            <button onClick={handleBack} className={ghostBtn}>
              <ArrowLeft size={15} /> Back
            </button>
          </div>

          {/* ── Receipt Type radios ── */}
          {!isEditMode && (
            <div className={`${cardBase} p-4 mb-5`}>
              <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                STORE RECEIPT TYPE
              </p>
              <div className="flex flex-wrap gap-6">
                {[
                  { label: "DIRECT RECEIPT",        value: RT.DIRECT   },
                  { label: "OWN WORKSHOP ITEMS",     value: RT.WORKSHOP },
                  { label: "RETURNED ITEMS",         value: RT.RETURNED },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="receiptType"
                      value={opt.value}
                      checked={receiptType === opt.value}
                      onChange={() => setReceiptType(opt.value)}
                      disabled={isEditMode}
                      className="accent-blue-600 w-4 h-4"
                    />
                    <span className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Master fields: Receipt No / Date / Store / Project ── */}
          <div className={`${cardBase} p-5 mb-5`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              {/* Receipt No */}
              {isEditMode && receiptNo && (
                <div>
                  <label className={labelBase}><Hash size={13} className="inline mr-1"/>Store Receipt No</label>
                  <div className={`mt-1.5 px-4 py-2.5 rounded-xl text-sm font-bold ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                    {receiptNo}
                  </div>
                </div>
              )}

              {/* Date */}
              <div>
                <label className={labelBase}><Calendar size={13} className="inline mr-1"/>Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={entryDate}
                  onChange={e => handleEntryDateChange(e.target.value)}
                  disabled={isApproved}
                  max={todayIso()}
                  className={inputBase + " mt-1.5"}
                />
              </div>

              {/* Store */}
              <div>
                <label className={labelBase}><Store size={13} className="inline mr-1"/>Store</label>
                <select
                  value={storeId}
                  onChange={e => setStoreId(e.target.value)}
                  disabled={isApproved}
                  className={selectBase + " mt-1.5"}
                >
                  <option value="Select">Select</option>
                  {storeList.map(st => (
                    <option key={st.value} value={st.value}>{st.label}</option>
                  ))}
                </select>
              </div>

              {/* Project */}
              <div>
                <label className={labelBase}><FolderKanban size={13} className="inline mr-1"/>Project Name</label>
              <select
  value={projectId}
  onChange={(e) => setProjectId(e.target.value)}
  className={selectBase + " mt-1.5"}
>
  <option value="Select">Select</option>
  {projectList.map((p) => (
    <option
      key={p.value || p.fld_projectid}
      value={p.value || p.fld_projectid}
    >
      {p.label || p.fld_projectname}
    </option>
  ))}
</select>
              </div>
            </div>
          </div>

          {/* ── FILTRATION AREA ── (pnl_filteration) */}
          {showFiltration && (
            <div className={`${cardBase} p-5 mb-5`}>
              <p className={`text-xs font-bold uppercase tracking-widest mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                FILTRATION AREA
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* Item Category — only for Direct Purchase */}
                {showCategory && (
                  <div>
                    <label className={labelBase}><Tag size={13} className="inline mr-1"/>Item Category</label>
                    <select
  value={categoryId}
  onChange={(e) => setCategoryId(e.target.value)}
  className={selectBase + " mt-1.5"}
>
  <option value="Select">Select</option>
  {categoryList.map((c) => (
    <option key={c.value} value={c.value}>
      {c.label}
    </option>
  ))}
</select>
                  </div>
                )}

                {/* Item Code — autocomplete */}
                <div className="relative">
                  <label className={labelBase}><Code2 size={13} className="inline mr-1"/>Item Code</label>
                  <input
                    value={itemCode}
                    onChange={e => { setItemCode(e.target.value); fetchAcCode(e.target.value); }}
                    onBlur={() => { setTimeout(() => setAcCodeOpen(false), 150); handleItemFilterChange(); }}
                    autoComplete="off"
                    placeholder="Type item code..."
                    className={inputBase + " mt-1.5"}
                  />
                  {acCodeOpen && acCodeItems.length > 0 && (
                    <div className={`absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-auto max-h-44 ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {acCodeItems.map((x, i) => (
                        <button key={i} type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { setItemCode(x); setAcCodeOpen(false); doSearch(); }}
                          className={`w-full text-left px-4 py-2.5 text-sm ${isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"}`}>
                          {x}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Item Name — autocomplete */}
                <div className="relative">
                  <label className={labelBase}><AlignLeft size={13} className="inline mr-1"/>Item Name</label>
                  <input
                    value={itemName}
                    onChange={e => { setItemName(e.target.value); fetchAcName(e.target.value); }}
                    onBlur={() => { setTimeout(() => setAcNameOpen(false), 150); handleItemFilterChange(); }}
                    autoComplete="off"
                    placeholder="Type item name..."
                    className={inputBase + " mt-1.5"}
                  />
                  {acNameOpen && acNameItems.length > 0 && (
                    <div className={`absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-auto max-h-44 ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {acNameItems.map((x, i) => (
                        <button key={i} type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { setItemName(x); setAcNameOpen(false); doSearch(); }}
                          className={`w-full text-left px-4 py-2.5 text-sm ${isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"}`}>
                          {x}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* From / To Date — only for Returned */}
                {showFromToDate && (
                  <>
                    <div>
                      <label className={labelBase}><Calendar size={13} className="inline mr-1"/>From Date</label>
                      <input type="date" value={fromDate}
                        onChange={e => { setFromDate(e.target.value); if (itemCode || itemName) doSearch(); }}
                        max={todayIso()} className={inputBase + " mt-1.5"} />
                    </div>
                    <div>
                      <label className={labelBase}><Calendar size={13} className="inline mr-1"/>To Date</label>
                      <input type="date" value={toDate}
                        onChange={e => { setToDate(e.target.value); if (itemCode || itemName) doSearch(); }}
                        max={todayIso()} className={inputBase + " mt-1.5"} />
                    </div>
                  </>
                )}
              </div>

              {/* Search button */}
              <div className="mt-4 flex justify-start">
                <button onClick={doSearch} className={primaryBtn} disabled={loading}>
                  <Search size={14} /> {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          {/* ── gv_items: Workshop / Returned issue list (pnl_ownworkshop) ── */}
         {showIssueGrid && (
            <div className={`${cardBase} mb-5 overflow-x-auto`}>
              <p className={`px-5 pt-4 text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {receiptType === RT.WORKSHOP ? "WORKSHOP ENTRIES" : "RETURNED ISSUE ENTRIES"}
              </p>

              {/* Loading state */}
              {loading && issueRows.length === 0 && (
                <div className={`px-5 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  <RefreshCcw className="w-5 h-5 animate-spin mx-auto mb-2 opacity-50" />
                  Loading entries...
                </div>
              )}

              {/* Empty state — only show after load completes */}
              {!loading && issueRows.length === 0 && receiptType === RT.WORKSHOP && (
                <div className={`px-5 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No workshop entries found for this branch.
                </div>
              )}

              {/* Empty state for Returned — prompt user to search */}
              {!loading && issueRows.length === 0 && receiptType === RT.RETURNED && (
                <div className={`px-5 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Use the filters above and click <b>Search</b> to find returned issue entries.
                </div>
              )}

              {/* Data table — only when rows exist */}
              {issueRows.length > 0 && (
                <table className="w-full mt-3">
                  <thead className={`${primaryBtnClass}`}>
                    <tr>
                      <th className={thClass}>S.No</th>
                      <th className={thClass}>Entry No</th>
                      <th className={thClass}>Date</th>
                      <th className={thClass}>Site Name</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {issueRows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "hover:bg-white/5" : "hover:bg-blue-50"} transition`}>
                        <td className={tdClass}>{idx + 1}</td>
                        <td className={tdClass}>
                          <button
                            onClick={() => handleIssueRowClickFull(r)}
                            className="text-blue-500 font-bold hover:underline text-sm"
                          >
                            {r.Nos || r.nos || r.EntryNo}
                          </button>
                        </td>
                        <td className={tdClass}>{r.Date || r.date}</td>
                        <td className={tdClass}>{r.Name || r.name || r.SiteName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── lst_items: Direct Purchase listbox (pnl_purhcaseissue) ── */}
          {showListBox && listBoxItems.length > 0 && (
            <div className="mb-5">
              <div className={`${cardBase} p-4`}>
                <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  SELECT ITEMS TO ADD
                </p>
                <div className={`border rounded-xl overflow-auto max-h-44 ${isDark ? "border-white/10 bg-gray-800/30" : "border-gray-200 bg-gray-50"}`}>
                  {listBoxItems.map((item, i) => (
                    <div key={i}
                      onClick={() => toggleListBox(item)}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm transition ${
                        selectedListBox.has(item)
                          ? (isDark ? "bg-blue-900/40 text-blue-200" : "bg-blue-100 text-blue-800")
                          : (isDark ? "text-gray-200 hover:bg-white/5" : "text-gray-800 hover:bg-gray-100")
                      }`}>
                      <CheckSquare size={14} className={selectedListBox.has(item) ? "text-blue-500" : "text-gray-400"} />
                      {item}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-center">
                  <button onClick={handleAddItems} className={primaryBtn} disabled={loading}>
                    <Plus size={14} /> {loading ? "Adding..." : "Add Selected Items"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Info message (FIFO) ── */}
          {infoMessage && (
            <div className={`mb-4 px-5 py-3 rounded-xl text-sm font-semibold border ${
              isDark ? "bg-yellow-900/20 text-yellow-300 border-yellow-700/30" : "bg-yellow-50 text-yellow-700 border-yellow-200"
            }`}>
              ⚠ {infoMessage}
            </div>
          )}

          {/* ── gv_branchitemmaster: Detail Items Grid ── */}
          {showDetailGrid && (
            <div className={`${cardBase} mb-5`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${primaryBtnClass}`}>
                    <tr>
                      <th className={thClass}>S.No</th>
                      <th className={thClass}>Item Code</th>
                      <th className={thClass}>Item Name</th>
                      <th className={thClass}>Part No</th>
                      <th className={thClass}>UOM</th>
                      {receiptType === RT.RETURNED && <th className={thClass}>Issued Qty</th>}
                      <th className={thClass}>Qty</th>
                      <th className={thClass}>Price</th>
                      {receiptType !== RT.DIRECT && <th className={thClass}>Dept Code:Name</th>}
                      {receiptType !== RT.DIRECT && <th className={thClass}>Vehicle Code:Name</th>}
                      <th className={thClass}>Serial No</th>
                      <th className={thClass}>Receive From</th>
                      <th className={thClass}>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox"
                            onChange={e => handleSelectAll(e.target.checked)}
                            className="accent-white w-3.5 h-3.5"
                            disabled={isApproved}
                          />
                          <span className="text-xs">All</span>
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {pagedDetailRows.length === 0 ? (
                      <tr>
                        <td colSpan={13} className={`px-4 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          No items added yet
                        </td>
                      </tr>
                    ) : pagedDetailRows.map((row, pageIdx) => {
                      const globalIdx = (detailPage - 1) * DETAIL_PAGE_SIZE + pageIdx;
                      const canSerial = row.fld_IsSerialNo === 1;
                      return (
                        <tr key={globalIdx} className={`transition ${
                          globalIdx % 2 === 0
                            ? (isDark ? "bg-white/5" : "bg-gray-50")
                            : (isDark ? "bg-transparent" : "bg-white")
                        }`}>
                          <td className={tdClass}>{globalIdx + 1}</td>
                          <td className={tdClass}>{row.ItemCode}</td>
                          <td className={tdClass}>{row.ItemName}</td>
                          <td className={tdClass}>{row.PartNo || "—"}</td>
                          <td className={tdClass + " text-center"}>{row.fld_uomname}</td>
                          {receiptType === RT.RETURNED && (
                            <td className={tdClass + " text-center"}>{row.IssuedQty}</td>
                          )}
                          {/* Qty */}
                          <td className={tdClass}>
                            <input
                              type="number"
                              value={row.Qty}
                              onChange={e => updateDetailRow(globalIdx, "Qty", e.target.value)}
                              onBlur={() => recalcTotal(detailRows)}
                              disabled={isApproved || row._workshopQtyDisabled}
                              min="0" step="0.001"
                              className={`w-20 px-2 py-1.5 rounded-lg border text-sm outline-none transition ${
                                isDark
                                  ? "bg-gray-700/50 border-white/10 text-white focus:ring-1 focus:ring-blue-500"
                                  : "bg-white border-gray-300 text-gray-900 focus:ring-1 focus:ring-blue-500"
                              } ${isApproved || row._workshopQtyDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            />
                          </td>
                          {/* Price */}
                          <td className={tdClass}>
                            <input
                              type="number"
                              value={row.Price}
                              onChange={e => updateDetailRow(globalIdx, "Price", e.target.value)}
                              onBlur={() => recalcTotal(detailRows)}
                              disabled={isApproved}
                              min="0" step="0.0001"
                              className={`w-24 px-2 py-1.5 rounded-lg border text-sm outline-none transition ${
                                isDark
                                  ? "bg-gray-700/50 border-white/10 text-white focus:ring-1 focus:ring-blue-500"
                                  : "bg-white border-gray-300 text-gray-900 focus:ring-1 focus:ring-blue-500"
                              } ${isApproved ? "opacity-50 cursor-not-allowed" : ""}`}
                            />
                          </td>
                          {/* Department */}
                          {receiptType !== RT.DIRECT && (
                            <td className={tdClass}>{row.Department || "—"}</td>
                          )}
                          {/* Vehicle */}
                          {receiptType !== RT.DIRECT && (
                            <td className={tdClass}>{row.Vehicle || "—"}</td>
                          )}
                          {/* Serial No linkbutton */}
                          <td className={tdClass + " text-center"}>
                            {canSerial ? (
                              <button
                                onClick={() => handleSerialNoClick(row, globalIdx)}
                                disabled={isApproved}
                                className="inline-flex items-center gap-1 text-blue-500 hover:underline text-xs font-semibold disabled:opacity-50"
                              >
                                <ExternalLink size={11} /> {row.serialno || "Enter Serial No"}
                              </button>
                            ) : (
                              <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>—</span>
                            )}
                          </td>
                          {/* Receive From */}
                          <td className={tdClass}>
                            <input
                              type="text"
                              value={row.fld_ReceiveFrom}
                              onChange={e => updateDetailRow(globalIdx, "fld_ReceiveFrom", e.target.value)}
                              disabled={isApproved}
                              className={`w-24 px-2 py-1.5 rounded-lg border text-sm outline-none transition ${
                                isDark
                                  ? "bg-gray-700/50 border-white/10 text-white focus:ring-1 focus:ring-blue-500"
                                  : "bg-white border-gray-300 text-gray-900 focus:ring-1 focus:ring-blue-500"
                              } ${isApproved ? "opacity-50 cursor-not-allowed" : ""}`}
                            />
                          </td>
                          {/* Select checkbox */}
                          <td className={tdClass + " text-center"}>
                            <input
                              type="checkbox"
                              checked={!!row.IsSelected}
                              onChange={e => toggleDetailSelect(globalIdx, e.target.checked)}
                              disabled={isApproved || row._chkDisabled}
                              className="accent-blue-600 w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination for detail grid */}
              {detailTotalPages > 1 && (
                <div className={`px-5 py-4 flex flex-col md:flex-row items-center justify-between gap-3 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Showing {(detailPage-1)*DETAIL_PAGE_SIZE+1}–{Math.min(detailPage*DETAIL_PAGE_SIZE, detailRows.length)} of {detailRows.length}
                  </span>
                  <div className="inline-flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                    <button onClick={() => setDetailPage(p => Math.max(1, p-1))} disabled={detailPage === 1}
                      className={`px-3 py-1.5 text-xs font-bold ${detailPage===1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"}`}>
                      Prev
                    </button>
                    {detailPageButtons.map(pno => (
                      <button key={pno} onClick={() => setDetailPage(pno)}
                        className={`px-3 py-1.5 text-xs font-bold transition ${detailPage===pno ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"}`}>
                        {pno}
                      </button>
                    ))}
                    <button onClick={() => setDetailPage(p => Math.min(detailTotalPages, p+1))} disabled={detailPage === detailTotalPages}
                      className={`px-3 py-1.5 text-xs font-bold ${detailPage===detailTotalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"}`}>
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Total Value ── */}
          {showDetailGrid && (
            <div className={`${cardBase} p-4 mb-5`}>
              <div className="flex items-center gap-4">
                <label className={`font-bold text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Total Value
                </label>
                <input
                  readOnly
                  value={totalValue}
                  disabled={isApproved}
                  className={`px-4 py-2.5 rounded-xl border text-sm font-bold w-40 outline-none ${
                    isDark
                      ? "bg-gray-800/40 border-white/10 text-purple-300"
                      : "bg-gray-50 border-gray-200 text-purple-700"
                  } ${isApproved ? "opacity-60" : ""}`}
                />
              </div>
            </div>
          )}

          {/* ── Submit Button ── */}
          {showSubmitBtn && !isApproved && (
            <div className="flex justify-center">
              <button onClick={handleSubmit} className={primaryBtn} disabled={submitting || loading}>
                <Send size={14} /> {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}

          {/* Approved badge */}
          {isApproved && (
            <div className="flex justify-center mt-4">
              <div className={`px-6 py-3 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/30 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
                ✅ This receipt has been approved. No edits allowed.
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
}